/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// SafeCircle Live Activity — UID-keyed "reader index" backing the RTDB
// security rule for /users/{owner}/liveState/activity.
//
// WHY THIS EXISTS: SafeCircle members are stored under random push-ids at
// /users/{uid}/safeCircle/{memberId}, with the friend's UID only in an
// OPTIONAL `linkedFriendUid` field. RTDB security rules can't iterate
// children to find a matching UID, so they can't answer "is reader R in
// owner O's SafeCircle?" directly. We mirror that membership into a flat,
// UID-keyed index — /users/{owner}/safeCircleReaders/{friendUid} = true —
// which a rule CAN check with a simple .exists(). Only members that have a
// linkedFriendUid (i.e. real in-app friends) appear here; phone-only members
// have no UID to read with, so they're correctly absent.
//
// Pure helpers (computeReaderIndex / readerToClearOnRemoval) are unit-tested;
// the RTDB writers below are thin wrappers around them.

import { type Database, ref, remove, set, update } from "firebase/database";
import type { SafeCircleStaticMember } from "./staticMembers";

export function readersCollectionPath(ownerUid: string): string {
  return `users/${ownerUid}/safeCircleReaders`;
}

export function readerPath(ownerUid: string, friendUid: string): string {
  return `${readersCollectionPath(ownerUid)}/${friendUid}`;
}

/**
 * The desired index, derived purely from the member list: the set of
 * linkedFriendUids present among members. Returned as a `{ [uid]: true }`
 * map so it can be `set()` wholesale during a backfill.
 */
export function computeReaderIndex(
  members: SafeCircleStaticMember[],
): Record<string, true> {
  const index: Record<string, true> = {};
  for (const m of members) {
    if (m.linkedFriendUid) index[m.linkedFriendUid] = true;
  }
  return index;
}

/**
 * Given the FULL current member list (before removal) and the id being
 * removed, return the friendUid whose index entry must be cleared — or null
 * if nothing should be cleared. We clear ONLY when the removed member had a
 * linkedFriendUid AND no OTHER remaining member shares that same uid (a user
 * could add the same friend under two entries; removing one must not revoke
 * read access the other still grants).
 */
export function readerToClearOnRemoval(
  membersBeforeRemoval: SafeCircleStaticMember[],
  removedMemberId: string,
): string | null {
  const removed = membersBeforeRemoval.find(
    (m) => m.memberId === removedMemberId,
  );
  const uid = removed?.linkedFriendUid;
  if (!uid) return null;
  const stillReferenced = membersBeforeRemoval.some(
    (m) => m.memberId !== removedMemberId && m.linkedFriendUid === uid,
  );
  return stillReferenced ? null : uid;
}

/** Add a reader-index entry for a freshly-added member (no-op when the member
 *  has no linkedFriendUid). */
export async function addReaderIndexEntry(
  database: Database,
  ownerUid: string,
  friendUid: string | undefined,
): Promise<void> {
  if (!ownerUid || !friendUid) return;
  await set(ref(database, readerPath(ownerUid, friendUid)), true);
}

/** Clear a reader-index entry on removal, honoring the "still referenced"
 *  guard. */
export async function clearReaderIndexEntry(
  database: Database,
  ownerUid: string,
  membersBeforeRemoval: SafeCircleStaticMember[],
  removedMemberId: string,
): Promise<void> {
  const uid = readerToClearOnRemoval(membersBeforeRemoval, removedMemberId);
  if (!uid) return;
  await remove(ref(database, readerPath(ownerUid, uid)));
}

/**
 * One-shot reconcile of the whole index from the current member list. Called
 * once when sharing is active (ActivityContext) so existing members written
 * before this feature shipped get an index entry. Uses a single `update()`
 * keyed by uid; entries no longer backed by a member are set to null so they
 * get removed in the same write.
 */
export async function backfillSafeCircleReaders(
  database: Database,
  ownerUid: string,
  members: SafeCircleStaticMember[],
  existingReaderUids: string[] = [],
): Promise<void> {
  if (!ownerUid) return;
  const desired = computeReaderIndex(members);
  const patch: Record<string, true | null> = { ...desired };
  for (const uid of existingReaderUids) {
    if (!desired[uid]) patch[uid] = null;
  }
  if (Object.keys(patch).length === 0) return;
  await update(ref(database, readersCollectionPath(ownerUid)), patch);
}
